import React, { useEffect, useMemo, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { Alert, Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';

import FlatImageEditor from '~/components/Common/Images/FlatImageEditor';
import { mergeFlatItemsWithLegacy, toFlatImageItem } from '~/components/Common/Images/legacyImageAdapter';
import { FlatImageItem } from '~/components/Common/Images/types';

import { useGetPropertyImages } from '~/hooks/apis/accommodation/useGetPropertyImages';
import { useUpdatePropertyImages } from '~/hooks/apis/accommodation/useUpdatePropertyImages';

import { AdminPropertyImageResponse } from '~/services/AccommodationService';

interface Props {
  propertyId: string;
  propertyName?: string;
}

function comparePropertyImages<T extends Pick<AdminPropertyImageResponse, 'order' | 'imageId'>>(left: T, right: T) {
  if (left.order === right.order) {
    return left.imageId.localeCompare(right.imageId);
  }

  return left.order - right.order;
}

function sortPropertyImages<T extends Pick<AdminPropertyImageResponse, 'order' | 'imageId'>>(images: Array<T>) {
  return [...images].sort(comparePropertyImages);
}

type EditablePropertyImage = AdminPropertyImageResponse & {
  id: string;
  publicImageId: string;
  hidden: boolean;
};

function toEditablePropertyImage(image: AdminPropertyImageResponse): EditablePropertyImage {
  return {
    ...image,
    id: image.imageId,
    publicImageId: image.imageId,
    hidden: image.hidden ?? false,
  };
}

function getSourceLabel(image: EditablePropertyImage) {
  if (image.source !== 'sync') {
    return 'Manual';
  }

  return image.sourceService ?? 'Sync';
}

function renderMetadataChips(image: EditablePropertyImage) {
  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
      <Chip size="small" label={`Source: ${getSourceLabel(image)}`} />
    </Stack>
  );
}

export default function AccommodationPropertyImagesPanel({ propertyId, propertyName }: Props) {
  const { data, isLoading, error } = useGetPropertyImages({
    propertyId,
    enabled: !!propertyId,
  });
  const updatePropertyImagesMutation = useUpdatePropertyImages();
  const [draftImages, setDraftImages] = useState<Array<EditablePropertyImage>>([]);
  const hasInitialisedDraftImagesRef = useRef(false);

  const displayPropertyName = data?.propertyName ?? propertyName ?? 'this property';
  const sortedImages = useMemo(() => sortPropertyImages(draftImages), [draftImages]);
  const sortedPersistedImages = useMemo(
    () => sortPropertyImages((data?.images ?? []).map(toEditablePropertyImage)),
    [data?.images],
  );
  const hasChanges = !isEqual(sortedImages, sortedPersistedImages);
  const hiddenImagesCount = sortedImages.filter((image) => image.hidden).length;

  useEffect(() => {
    if (!data) {
      return;
    }

    // Resync the draft after the mutation refreshes cached data, but avoid clobbering unsaved edits.
    if (!hasInitialisedDraftImagesRef.current || !hasChanges) {
      setDraftImages(data.images.map(toEditablePropertyImage));
      hasInitialisedDraftImagesRef.current = true;
    }
  }, [data, hasChanges]);

  const handleItemsChange = (nextItems: Array<FlatImageItem>) => {
    setDraftImages((currentImages) =>
      mergeFlatItemsWithLegacy(currentImages, nextItems).map((image) => ({
        ...image,
        id: image.imageId,
        publicImageId: image.imageId,
        hidden: image.hidden ?? false,
      })),
    );
  };

  const handleSave = async () => {
    try {
      await updatePropertyImagesMutation.mutateAsync({
        propertyId,
        images: sortedImages.map((image) => ({
          imageId: image.imageId,
          title: image.title,
          hidden: image.hidden,
        })),
      });
    } catch {
      // Mutation-level error handling already surfaces feedback to the user.
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load property images: {error.message}</Alert>;
  }

  if (!data || sortedImages.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No internal property images are currently stored for {displayPropertyName}.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          {sortedImages.length} persisted property images
          {hiddenImagesCount > 0 ? ` (${hiddenImagesCount} hidden)` : ''}.
        </Typography>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!hasChanges || updatePropertyImagesMutation.isPending}
        >
          {updatePropertyImagesMutation.isPending ? 'Saving...' : 'Save changes'}
        </Button>
      </Stack>

      <Box>
        <FlatImageEditor
          deleteActionLabel="Delete image"
          descriptionLabel="Title"
          disabled={updatePropertyImagesMutation.isPending}
          emptyStateMessage={`No internal property images are currently stored for ${displayPropertyName}.`}
          items={sortedImages.map((image) => toFlatImageItem(image))}
          renderItemActions={(item) => {
            const image = sortedImages.find((currentImage) => currentImage.imageId === String(item.id));

            return image ? renderMetadataChips(image) : null;
          }}
          reorderHiddenToEndOnToggle={false}
          showDeleteAction={false}
          showDescriptionField
          showHiddenToggle
          showReorderAction={false}
          onItemsChange={handleItemsChange}
        />
      </Box>
    </Stack>
  );
}
